import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import {
  ActionButton,
  Background,
  CheckoutBox,
  MessagePage
} from '@appcharge/shared-ui';
import { Grid, Stack, Typography } from '@mui/material';
import useTheme from 'api/useTheme';
import { EStyleProperty } from 'constants/enums';
import { useFormikContext } from 'formik';
import { getStyledSelectItem } from 'utils/getStyledSelectItem';

import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { Separator } from 'components/ui/separator';

import { ThemeLoader } from '../ThemeLoader';

import { defaultCompletedTheme } from './defaults';
import { CompletedThemeFormValues } from './types';

import 'style/forms.scss';

export const CompletedTheme: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CompletedThemeFormValues>();

  const { publisherId } = useParams();

  const defaultValues = useMemo(() => {
    const defaults = {
      headerText: defaultCompletedTheme.headerText,
      headerColor: defaultCompletedTheme.headerColor,
      headerSize: defaultCompletedTheme.headerSize,
      headerWeight: defaultCompletedTheme.headerWeight,
      text: defaultCompletedTheme.text,
      textColor: defaultCompletedTheme.textColor,
      textSize: defaultCompletedTheme.textSize,
      textWeight: defaultCompletedTheme.textWeight,
      backToGameButtonText: defaultCompletedTheme.backToGameButtonText,
      deepLinks: defaultCompletedTheme.deepLinks
    };

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      headerText: values.headerText || defaultCompletedTheme.headerText,
      headerColor: values.headerColor || defaultCompletedTheme.headerColor,
      headerSize: values.headerSize || defaultCompletedTheme.headerSize,
      headerWeight: values.headerWeight || defaultCompletedTheme.headerWeight,
      text: values.text || defaultCompletedTheme.text,
      textColor: values.textColor || defaultCompletedTheme.textColor,
      textSize: values.textSize || defaultCompletedTheme.textSize,
      textWeight: values.textWeight || defaultCompletedTheme.textWeight,
      backToGameButtonText: values.backToGameButtonText || defaultCompletedTheme.backToGameButtonText,
      deepLinks: values.deepLinks || defaultCompletedTheme.deepLinks
    };
  }, [values]);

  const {
    getCompletedTheme: completeTheme,
    getGeneralTheme: generalTheme,
    getStoreScreen: screenTheme
  } = useTheme(publisherId || undefined);

  const hasValues = useMemo(() => Object.keys(values).length > 0, [values]);

  useEffect(
    () =>
      setIsLoading(
        completeTheme.isLoading || !completeTheme.data || !hasValues
      ),
    [completeTheme?.isLoading, completeTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Completed Page
        </Typography>
        <Stack gap={3}>
          <AcCard
            stackContainer={false}
            title="Confirm the purchase"
            description="Congratulate your players on a successful purchase and let them know what’s next"
          >
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  header="Header"
                  name="headerText"
                  value={defaultValues.headerText}
                  tooltip="This will be the header of the success screen."
                  type="text"
                  dataTestId="completedHeaderText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.headerText && Boolean(errors.headerText)}
                  helperText={
                    touched.headerText ? errors.headerText?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <AcGradientInput
                  header="Color"
                  name="headerColor"
                  defaultValue={defaultValues.headerColor}
                  setValue={setFieldValue}
                  onBlur={handleBlur}
                  dataTestId="completedHeaderColor"
                  dataTestPopoverId="completedHeaderColorPopover"
                  dataTestColorOneId="completedHeaderColorOne"
                  dataTestColorTwoId="completedHeaderColorTwo"
                  dataTestGradientTypeId="completedHeaderColorGradientType"
                  dataTestGradientDirectionId="completedHeaderColorGradientDirection"
                  error={touched.headerColor && Boolean(errors.headerColor)}
                  helperText={
                    touched.headerColor ? errors.headerColor?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <AcInput
                  header="Size"
                  name="headerSize"
                  type="number"
                  dataTestId="completedHeaderSize"
                  slotProps={{
                    htmlInput: {
                      max: 40,
                      min: 26
                    }
                  }}
                  value={defaultValues.headerSize}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.headerSize && Boolean(errors.headerSize)}
                  helperText={
                    touched.headerSize ? errors.headerSize?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <AcSelect
                  header="Weight"
                  name="headerWeight"
                  defaultValue="regular"
                  value={defaultValues.headerWeight}
                  dataTestBoxId="completedHeaderWeight"
                  dataTestListId="completedHeaderWeightList"
                  optionDataTestIdPrefix="completedHeaderWeightOption"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.headerWeight && Boolean(errors.headerWeight)}
                  items={[
                    {
                      content: 'Regular',
                      key: 'regular',
                      value: '300',
                      renderFunction: () =>
                        getStyledSelectItem(
                          'regular',
                          EStyleProperty.fontWeight
                        )
                    },
                    {
                      content: 'Medium',
                      key: 'medium',
                      value: '500',
                      renderFunction: () =>
                        getStyledSelectItem('medium', EStyleProperty.fontWeight)
                    },
                    {
                      content: 'Bold',
                      key: 'bold',
                      value: '700',
                      renderFunction: () =>
                        getStyledSelectItem('bold', EStyleProperty.fontWeight)
                    }
                  ]}
                />
              </Grid>
            </Grid>
          </AcCard>
          <AcCard stackContainer={false} title="Additional text">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  header="Text"
                  name="text"
                  value={defaultValues.text}
                  tooltip=""
                  type="text"
                  dataTestId="completedText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.text && Boolean(errors.text)}
                  helperText={touched.text ? errors.text?.toString() : ''}
                />
              </Grid>
              <Grid item xs={4}>
                <AcGradientInput
                  header="Color"
                  name="textColor"
                  defaultValue={{
                    colorOne: defaultValues.textColor
                  }}
                  setValue={setFieldValue}
                  onBlur={handleBlur}
                  dataTestId="completedTextColor"
                  dataTestPopoverId="completedTextColorPopover"
                  dataTestColorOneId="completedTextColorOne"
                  dataTestColorTwoId="completedTextColorTwo"
                  dataTestGradientTypeId="completedTextColorGradientType"
                  dataTestGradientDirectionId="completedTextColorGradientDirection"
                  error={touched.textColor && Boolean(errors.textColor)}
                  helperText={
                    touched.textColor ? errors.textColor?.toString() : ''
                  }
                  outputAsSingleColor
                  onlySolid
                />
              </Grid>
              <Grid item xs={4}>
                <AcInput
                  header="Size"
                  name="textSize"
                  type="number"
                  dataTestId="completedTextSize"
                  slotProps={{
                    htmlInput: {
                      max: 24,
                      min: 12
                    }
                  }}
                  value={defaultValues.textSize}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.textSize && Boolean(errors.textSize)}
                  helperText={
                    touched.textSize ? errors.textSize?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <AcSelect
                  header="Weight"
                  name="textWeight"
                  defaultValue="regular"
                  value={defaultValues.textWeight}
                  dataTestBoxId="completedTextWeight"
                  dataTestListId="completedTextWeightList"
                  optionDataTestIdPrefix="completedTextWeightOption"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.textWeight && Boolean(errors.textWeight)}
                  items={[
                    {
                      content: 'Regular',
                      key: 'regular',
                      value: '300',
                      renderFunction: () =>
                        getStyledSelectItem(
                          'regular',
                          EStyleProperty.fontWeight
                        )
                    },
                    {
                      content: 'Medium',
                      key: 'medium',
                      value: '500',
                      renderFunction: () =>
                        getStyledSelectItem('medium', EStyleProperty.fontWeight)
                    },
                    {
                      content: 'Bold',
                      key: 'bold',
                      value: '700',
                      renderFunction: () =>
                        getStyledSelectItem('bold', EStyleProperty.fontWeight)
                    }
                  ]}
                />
              </Grid>
            </Grid>
          </AcCard>
        </Stack>
        <Separator className="my-6 bg-base-border" />
        <Stack gap={3}>
          <AcCard stackContainer={false} title="Back to Game Button">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Back to Game Button Text"
                  name="backToGameButtonText"
                  value={defaultValues.backToGameButtonText}
                  dataTestId="completedBackToGameButtonText"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.backToGameButtonText &&
                    Boolean(errors.backToGameButtonText)
                  }
                  helperText={
                    touched.backToGameButtonText
                      ? errors.backToGameButtonText?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Android Back to Game Button Deep Link"
                  name="deepLinks.android"
                  value={defaultValues.deepLinks.android}
                  dataTestId="completedDeepLinksAndroid"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.android &&
                    Boolean(errors.deepLinks?.android)
                  }
                  helperText={
                    touched.deepLinks?.android
                      ? errors.deepLinks?.android?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Apple Back to Game Button Deep Link"
                  name="deepLinks.ios"
                  value={defaultValues.deepLinks.ios}
                  dataTestId="completedDeepLinksIos"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.ios && Boolean(errors.deepLinks?.ios)
                  }
                  helperText={
                    touched.deepLinks?.ios
                      ? errors.deepLinks?.ios?.toString()
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <AcInput
                  headerSize="14"
                  header="Web Back to Game Button Deep Link"
                  name="deepLinks.web"
                  value={defaultValues.deepLinks.web}
                  dataTestId="completedDeepLinksWeb"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.deepLinks?.web && Boolean(errors.deepLinks?.web)
                  }
                  helperText={
                    touched.deepLinks?.web
                      ? errors.deepLinks?.web?.toString()
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </AcCard>
        </Stack>
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="store-preview-wrapper iphone-mock">
          <Background
            backgroundImageDesktop={generalTheme?.data?.backgroundImageMobile}
            backgroundImageMobile={generalTheme?.data?.backgroundImageMobile}
            width="100%"
            height="100%"
            position="absolute"
          >
            <MessagePage
              headerText={defaultValues.headerText}
              headerColor={defaultValues.headerColor}
              headerSize={defaultValues.headerSize}
              headerWeight={defaultValues.headerWeight}
              text={[defaultValues.text]}
              textColor={defaultValues.textColor}
              textSize={defaultValues.textSize}
              textWeight={defaultValues.textWeight}
              fontFamily={generalTheme?.data?.font}
              preview={true}
            >
              <CheckoutBox height="135px" width="90%">
                <div></div>
              </CheckoutBox>
              <ActionButton
                action={() => null}
                text={defaultValues.backToGameButtonText}
                colors={screenTheme?.data?.buttonColor}
                textColor={screenTheme?.data?.buttonTextColor}
              />
            </MessagePage>
          </Background>
        </div>
      </Grid>
    </Grid>
  );
};
